import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Users, Bookmark, BookmarkCheck, ArrowLeft, Send, ChevronRight, Loader } from 'lucide-react';
import { fetchJob, fetchJobs } from '../services/api';
import { useApp } from '../context/AppContext';
import { JOB_TYPE_LABELS, labelOf, jobTitle, groupSkills } from '../constants/jobOptions';
import './JobDetailPage.css';

export default function JobDetailPage() {
  const { id } = useParams();
  const { user, openAuth, savedJobs, toggleSaveJob, appliedJobs, applyToJob } = useApp();

  const [job, setJob] = useState(null);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchJob(id)
      .then(({ data }) => {
        const raw = data.job || data;
        // Backend liefert company_name/job_type — auf die im UI verwendeten Felder mappen
        const jobData = {
          ...raw,
          company: raw.company || raw.company_name,
          type: labelOf(JOB_TYPE_LABELS, raw.job_type) || raw.type,
          applicants: raw.applicants ?? raw.applicant_count,
          posted: raw.posted || (raw.created_at ? new Date(raw.created_at).toLocaleDateString('de-DE') : ''),
        };
        setJob(jobData);
        // Load related jobs by category
        if (jobData.category) {
          fetchJobs({ category: jobData.category, limit: 4 })
            .then(({ data: relData }) => {
              const all = Array.isArray(relData) ? relData : relData.jobs || relData.data || [];
              setRelatedJobs(all.filter(j => String(j.id) !== String(id)).slice(0, 3));
            })
            .catch(() => {});
        }
      })
      .catch(() => setError('Dieser Job konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="job-detail-loading">
      <Loader size={32} className="spin" />
      <p>Job wird geladen...</p>
    </div>
  );

  if (error || !job) return (
    <div className="job-detail-notfound">
      <h2>{error || 'Job nicht gefunden'}</h2>
      <Link to="/jobs">← Zurück zu Jobs</Link>
    </div>
  );

  const isSaved = savedJobs.some(j => j.id === job.id);
  const isApplied = appliedJobs.some(j => j.id === job.id) || applied;

  const handleApply = async () => {
    if (!user) { openAuth('register'); return; }
    setApplying(true);
    setApplyError(null);
    try {
      await applyToJob(job);
      setApplied(true);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
    } catch (err) {
      setApplyError(err.response?.data?.message || 'Bewerbung konnte nicht abgeschickt werden.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="job-detail">
      <div className="container">
        <Link to="/jobs" className="job-detail__back">
          <ArrowLeft size={16} /> Zurück zu Jobs
        </Link>

        <div className="job-detail__layout">
          {/* Main */}
          <div className="job-detail__main">
            {showSuccess && (
              <div className="job-detail__success animate-fadeInUp">
                🎉 Bewerbung erfolgreich abgeschickt! Der Arbeitgeber wird sich bald melden.
              </div>
            )}
            {applyError && (
              <div className="job-detail__apply-error animate-fadeInUp">
                ⚠️ {applyError}
              </div>
            )}

            <div className="job-detail__card">
              <div className="job-detail__header">
                <div className="job-detail__logo">
                  {job.company?.charAt(0)}
                </div>
                <div className="job-detail__header-info">
                  <p className="job-detail__company">{job.company}</p>
                  <h1 className="job-detail__title">{jobTitle(job.title)}</h1>
                  <div className="job-detail__meta">
                    <span><MapPin size={14} /> {job.location}</span>
                    <span><Clock size={14} /> {job.posted || job.createdAt}</span>
                    <span><Users size={14} /> {job.applicants ?? job.applicantCount ?? 0} Bewerber</span>
                  </div>
                </div>
                <div className="job-detail__actions-top">
                  <button
                    className={`job-detail__save-btn ${isSaved ? 'saved' : ''}`}
                    onClick={() => {
                      if (!user) { openAuth('register'); return; }
                      toggleSaveJob(job);
                    }}
                  >
                    {isSaved ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
                    {isSaved ? 'Gespeichert' : 'Speichern'}
                  </button>
                </div>
              </div>

              <div className="job-detail__tags">
                <span className="job-detail__tag job-detail__tag--type">{job.type}</span>
                <span className="job-detail__tag">{job.category}</span>
                {(job.tags || []).map(t => <span key={t} className="job-detail__tag">{t}</span>)}
                {job.isNew && <span className="job-detail__tag job-detail__tag--new">Neu</span>}
              </div>

              <div className="job-detail__salary-box">
                <span className="job-detail__salary-label">Vergütung</span>
                <span className="job-detail__salary-value">{job.salary || 'Auf Anfrage'}</span>
              </div>

              <div className="job-detail__section">
                <h2>Beschreibung</h2>
                <p>{job.description}</p>
              </div>

              {job.requirements && (
                <div className="job-detail__section">
                  <h2>Anforderungen</h2>
                  <p style={{ whiteSpace: 'pre-line' }}>{job.requirements}</p>
                </div>
              )}

              {job.skills?.length > 0 && (
                <div className="job-detail__section">
                  <h2>Skills</h2>
                  {groupSkills(job.skills).map(group => (
                    <div key={group.title} className="job-detail__skill-group">
                      <h3>{group.title}</h3>
                      <div className="job-detail__tags">
                        {group.skills.map(s => <span key={s} className="job-detail__tag">{s}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {relatedJobs.length > 0 && (
              <div className="job-detail__related">
                <h3>Ähnliche Jobs</h3>
                <div className="job-detail__related-list">
                  {relatedJobs.map(rj => (
                    <Link key={rj.id} to={`/jobs/${rj.id}`} className="related-job-item">
                      <div>
                        <p className="related-job-title">{jobTitle(rj.title)}</p>
                        <p className="related-job-meta">{rj.company} · {rj.location}</p>
                      </div>
                      <ChevronRight size={16} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="job-detail__sidebar">
            <div className="job-detail__apply-card">
              <div className="job-detail__apply-salary">{job.salary || 'Auf Anfrage'}</div>
              <p className="job-detail__apply-type">{job.type} · {job.location}</p>

              {isApplied ? (
                <div className="job-detail__applied">
                  <span>✅</span>
                  <p>Bewerbung abgeschickt!</p>
                </div>
              ) : (
                <button
                  className="job-detail__apply-btn"
                  onClick={handleApply}
                  disabled={applying}
                >
                  {applying ? <Loader size={16} className="spin" /> : <Send size={16} />}
                  {applying ? 'Wird gesendet...' : 'Jetzt bewerben'}
                </button>
              )}

              <Link to="/chat" className="job-detail__chat-btn">
                💬 Frage stellen
              </Link>

              <div className="job-detail__company-info">
                <div className="job-detail__company-logo">{job.company?.charAt(0)}</div>
                <div>
                  <p className="job-detail__company-name">{job.company}</p>
                </div>
              </div>

              <div className="job-detail__info-list">
                <div className="job-detail__info-item">
                  <span>Standort</span>
                  <strong>{job.location}</strong>
                </div>
                <div className="job-detail__info-item">
                  <span>Arbeitsmodell</span>
                  <strong>{job.type}</strong>
                </div>
                <div className="job-detail__info-item">
                  <span>Beworben</span>
                  <strong>{job.applicants ?? job.applicantCount ?? 0} Personen</strong>
                </div>
                <div className="job-detail__info-item">
                  <span>Publiziert</span>
                  <strong>{job.posted || job.createdAt}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: fixierter Bewerben-Button direkt über der Bottom-Navigation */}
      <div className="job-detail__mobile-bar">
        {isApplied ? (
          <div className="job-detail__applied">
            <span>✅</span>
            <p>Bewerbung abgeschickt!</p>
          </div>
        ) : (
          <button
            className="job-detail__apply-btn"
            onClick={handleApply}
            disabled={applying}
          >
            {applying ? <Loader size={16} className="spin" /> : <Send size={16} />}
            {applying ? 'Wird gesendet...' : 'Jetzt bewerben'}
          </button>
        )}
      </div>
    </div>
  );
}
